import { useEffect, useState } from 'react';
import Layout from './Layout';
import Loading from './Loading';
import { supabase } from '../client/client';
import { useLogin } from '../context/LoginContext';
import { useNavigate } from 'react-router-dom';

const ReasignacionMasiva = () => {
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState({});
  const [savedRows, setSavedRows]   = useState({});
  const [equiposList, setEquiposList] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selections, setSelections] = useState({});
  const { getSessionAuth }          = useLogin();
  const navigate                    = useNavigate();

  useEffect(() => {
    const init = async () => {
      const session = await getSessionAuth();
      if (!session) { navigate('/'); return; }
      await loadData();
      setLoading(false);
    };
    init();
  }, []);

  const loadData = async () => {
    // Fetch current equipos for dropdown
    const { data: equipos } = await supabase
      .from('equipos')
      .select('id, title, attendant')
      .order('title');

    setEquiposList(equipos || []);
    const currentIds = new Set((equipos || []).map(e => e.id));

    // Fetch id_equipo from all four tables
    const [{ data: bk }, { data: mt }, { data: bs }, { data: lc }] = await Promise.all([
      supabase.from('backups').select('id_equipo'),
      supabase.from('maintenance').select('id_equipo'),
      supabase.from('backups_server').select('id_equipo'),
      supabase.from('licencias').select('id_equipo'),
    ]);

    // Group orphaned UUIDs with record counts per table
    const groups = {};

    const addTo = (rows, field) => {
      (rows || []).forEach(r => {
        if (r.id_equipo && !currentIds.has(r.id_equipo)) {
          if (!groups[r.id_equipo]) groups[r.id_equipo] = { backups: 0, maintenance: 0, backups_server: 0, licencias: 0 };
          groups[r.id_equipo][field]++;
        }
      });
    };

    addTo(bk, 'backups');
    addTo(mt, 'maintenance');
    addTo(bs, 'backups_server');
    addTo(lc, 'licencias');

    const result = Object.entries(groups)
      .map(([uuid, counts]) => ({ uuid, ...counts }))
      .sort((a, b) =>
        (b.backups + b.maintenance + b.backups_server + b.licencias) -
        (a.backups + a.maintenance + a.backups_server + a.licencias)
      );

    setOrphanGroups(result);
  };

  const handleSelect = (uuid, equipoId) => {
    setSelections(prev => ({ ...prev, [uuid]: equipoId }));
  };

  const handleSave = async (uuid) => {
    const equipoId = selections[uuid];
    if (!equipoId) return;
    const equipo = equiposList.find(e => e.id === equipoId);
    if (!equipo) return;

    setSaving(prev => ({ ...prev, [uuid]: true }));

    const updates = { equipo_title: equipo.title, equipo_attendant: equipo.attendant };
    const group   = orphanGroups.find(g => g.uuid === uuid);

    await Promise.all([
      group.backups > 0        && supabase.from('backups').update(updates).eq('id_equipo', uuid),
      group.maintenance > 0    && supabase.from('maintenance').update(updates).eq('id_equipo', uuid),
      group.backups_server > 0 && supabase.from('backups_server').update(updates).eq('id_equipo', uuid),
      group.licencias > 0      && supabase.from('licencias').update(updates).eq('id_equipo', uuid),
    ].filter(Boolean));

    setSaving(prev => ({ ...prev, [uuid]: false }));
    setSavedRows(prev => ({ ...prev, [uuid]: equipo.title + ' — ' + equipo.attendant }));
  };

  const pendingCount  = orphanGroups.filter(g => !savedRows[g.uuid]).length;
  const totalRecords  = orphanGroups.reduce((s, g) => s + g.backups + g.maintenance + g.backups_server + g.licencias, 0);
  const savedCount    = orphanGroups.filter(g => savedRows[g.uuid]).length;

  const [equipoSearch, setEquipoSearch] = useState('');
  const [showEquipos, setShowEquipos]   = useState(true);

  const filteredEquipos = equiposList.filter(eq =>
    !equipoSearch ||
    eq.title.toLowerCase().includes(equipoSearch.toLowerCase()) ||
    eq.attendant.toLowerCase().includes(equipoSearch.toLowerCase())
  );

  return (
    <Layout>
      {loading && <Loading />}

      {/* Header */}
      <div className="mb-6">
        <p className="text-[#4691A6] font-semibold text-sm uppercase tracking-wide">Herramienta de administración</p>
        <h1 className="text-gray-800 font-bold text-3xl mt-1">Reasignación masiva de equipos</h1>
        <p className="text-gray-500 text-sm mt-2">
          Asigna el equipo correcto a cada grupo de registros históricos.
          Al guardar, se actualiza el nombre y encargado en todos los registros de ese grupo.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Grupos huérfanos</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{orphanGroups.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Registros afectados</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{totalRecords}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Completados</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{savedCount} / {orphanGroups.length}</p>
        </div>
      </div>

      {!loading && (
        <div className="flex gap-5 items-start">

          {/* LEFT: orphan groups table */}
          <div className="flex-1 min-w-0 overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">#</th>
                  <th className="px-4 py-3 text-left font-semibold">ID anterior</th>
                  <th className="px-4 py-3 text-center font-semibold">Bk</th>
                  <th className="px-4 py-3 text-center font-semibold">Mt</th>
                  <th className="px-4 py-3 text-center font-semibold">BkS</th>
                  <th className="px-4 py-3 text-center font-semibold">Sw</th>
                  <th className="px-4 py-3 text-left font-semibold">Equipo actual</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orphanGroups.map((group, idx) => (
                  <tr
                    key={group.uuid}
                    className={savedRows[group.uuid] ? 'bg-green-50' : 'bg-white hover:bg-gray-50 transition-colors'}
                  >
                    <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400 select-all whitespace-nowrap">
                      {group.uuid}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {group.backups > 0
                        ? <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-bold">{group.backups}</span>
                        : <span className="text-gray-200">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {group.maintenance > 0
                        ? <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-bold">{group.maintenance}</span>
                        : <span className="text-gray-200">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {group.backups_server > 0
                        ? <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-bold">{group.backups_server}</span>
                        : <span className="text-gray-200">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {group.licencias > 0
                        ? <span className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full text-xs font-bold">{group.licencias}</span>
                        : <span className="text-gray-200">—</span>}
                    </td>
                    <td className="px-4 py-3 min-w-[240px]">
                      {savedRows[group.uuid] ? (
                        <span className="text-green-700 font-semibold text-sm flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                          </svg>
                          {savedRows[group.uuid]}
                        </span>
                      ) : (
                        <select
                          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#4691A6] bg-white"
                          value={selections[group.uuid] || ''}
                          onChange={e => handleSelect(group.uuid, e.target.value)}
                        >
                          <option value="">— Selecciona equipo —</option>
                          {equiposList.map(eq => (
                            <option key={eq.id} value={eq.id}>
                              {eq.title} — {eq.attendant}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!savedRows[group.uuid] && (
                        <button
                          onClick={() => handleSave(group.uuid)}
                          disabled={!selections[group.uuid] || saving[group.uuid]}
                          className="bg-[#356674] hover:bg-[#4691A6] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
                        >
                          {saving[group.uuid] ? 'Guardando…' : 'Guardar'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {orphanGroups.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 mx-auto mb-3 text-green-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <p className="font-semibold text-green-600">¡Todo en orden!</p>
                <p className="text-sm mt-1">No hay registros huérfanos pendientes.</p>
              </div>
            )}
          </div>

          {/* RIGHT: equipos reference panel */}
          <div className="w-80 flex-shrink-0 rounded-xl border border-gray-200 bg-white sticky top-4">
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer select-none border-b border-gray-100"
              onClick={() => setShowEquipos(v => !v)}
            >
              <div>
                <p className="text-xs font-semibold text-[#356674] uppercase tracking-wide">Referencia</p>
                <p className="text-sm font-bold text-gray-700 mt-0.5">Equipos actuales ({equiposList.length})</p>
              </div>
              <span className="text-gray-400 text-xs">{showEquipos ? '▲' : '▼'}</span>
            </div>

            {showEquipos && (
              <>
                <div className="px-3 py-2 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Buscar nombre o encargado…"
                    value={equipoSearch}
                    onChange={e => setEquipoSearch(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#4691A6]"
                  />
                  <p className="text-xs text-gray-400 mt-1">{filteredEquipos.length} resultado{filteredEquipos.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: '72vh' }}>
                  {filteredEquipos.map(eq => (
                    <div key={eq.id} className="px-3 py-2 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <p className="text-xs font-semibold text-gray-700 leading-tight">{eq.title}</p>
                      <p className="text-xs text-[#4691A6] mt-0.5">{eq.attendant}</p>
                      <p className="font-mono text-[10px] text-gray-300 mt-0.5 select-all leading-tight break-all">{eq.id}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

        </div>
      )}
    </Layout>
  );
};

export default ReasignacionMasiva;
